import {
  HereMapInterface,
  MapParams,
  ContainerRef,
  MapPadding,
  Zoom,
  CenterCoords,
  MapType,
  OnMapEventListener,
  MapEventListener,
  OnChangeBounds,
  OnChangeMapStreetView,
  ClickListener
} from '../types';
import { FitBounds } from 'common/types';
import { MapName } from 'entities/map';
import { Deck } from '@deck.gl/core';

import {
  handleClusterHover,
  getClusterParams
} from '../utils';

import config from 'config';

import { EMPTY_OBJECT } from 'shared/constants';
import { MAP_NAMES } from 'entities/map';
import { DEFAULT_MAP_CENTER, DEFAULT_MAP_ZOOM, MAP_TYPES } from '../constants';

export class HereMap implements HereMapInterface {
  name: MapName;
  overlay?: Deck;
  private _map?: Here;
  private _containerRef: ContainerRef;
  private _platform: any;
  private _tilePlatform: any;
  private _lang: string = 'en';
  private _mapUI?: any;
  private _defaultLayers: any;
  private _mapFeatures =
    'pois:disabled,environmental_zones:all,congestion_zones:all';
  private _pixelRatio = window.devicePixelRatio || 1;
  private _layers: Map<string, H.map.layer.TileLayer> = new Map();
  private _clickListeners: ClickListener[] = [];

  constructor(containerRef: ContainerRef) {
    this._containerRef = containerRef;
    this.name = MAP_NAMES.here;
  }

  initMap(params?: MapParams) {
    const { center = DEFAULT_MAP_CENTER, lang = 'en' } = params || {};
    this._lang = lang;

    try {
      if (this._containerRef) {
        this._platform = new window.H.service.Platform({
          apikey: config.hereAppKey,
        });

        // @ts-ignore
        const engineType = window.H.Map.EngineType['HARP'];

        const defaultLayers = this._platform.createDefaultLayers({
          lg: lang,
          pois: false,
          engineType
        });

        this._defaultLayers = defaultLayers;

        // new initialization
        this._map = new window.H.Map(
          this._containerRef,
          defaultLayers.vector.normal.map,
          {
            zoom: DEFAULT_MAP_ZOOM,
            engineType,
            center,
            pixelRatio: 1,
          }
        );

        this._map.getBaseLayer().setMin(4);
        this._map.getBaseLayer().setMax(22);

        // this._initWheelHandler();
        this._resizeMap();
        this._enableMapEvents();
        this._initMapUi(defaultLayers);

        this._initOverlay();
      }

    } catch (e) {
      console.log('e: ', e);
    }
  }

  _initOverlay() {
    this.overlay = new Deck({
      initialViewState: {
        latitude: 51.47,
        longitude: 0.45,
        zoom: 3,
        pitch: 0
      },
      onHover: (event) => {
        handleClusterHover(event, this._containerRef);
      },
      onClick: (event: any) => {       
        if (event.object?.cluster) {
          const clusterParams = getClusterParams(event);
          this._revealCluster(clusterParams);
        }
        
        const { object, layer = {} } = event;
        let markerType: string | undefined;

        if (!object) {
          markerType = undefined;
        } else if (object && object.data) {
          markerType = 'marker';
        } else {
          markerType = 'cluster';
        }

        this._clickListeners.forEach(fn => {
          fn({
            markerType,
            layerName: layer?.id,
            data: (object || EMPTY_OBJECT).data
          })
        });
      },
      parent: this._map.getElement(),
      controller: false,
      layers: []
    });

    this._map.addEventListener('mapviewchange', (event: any) => {
      const { height, width } = event.currentTarget.getViewPort();
      const zoom = event.currentTarget.getZoom();
      const viewPort = this._map.getViewPort();

      const { lat, lng } = this._map.screenToGeo(
        viewPort.center.x - ((viewPort.padding.left - viewPort.padding.right) / 2),
        viewPort.center.y - ((viewPort.padding.top - viewPort.padding.bottom) / 2)
      );

      this.overlay?.setProps({
        width,
        height,
        viewState: {
          latitude: lat,
          longitude: lng,
          zoom: zoom - 1
        }
      });
      // @ts-ignore
      this.overlay?.redraw();
    });
  }

  _initWheelHandler() {
    let controlTimeout: NodeJS.Timeout;

    this._map.addEventListener('wheel', (event: any) => {
      if (!event.defaultPrevented) {
        let { originalEvent } = event;
        const { deltaY } = originalEvent;
        let f: any = 0;
        let n = (w: any) => w;

        if (originalEvent.ctrlKey) {
          f = 1;
          n = (w: any) => w * (false ? 40 : 20);
        }

        const h = Math.min(f ? 100 : 200, n(Math.abs(deltaY)));
        let v = 0 > deltaY ? -1 : 1;
        const moveZ = .1 * -v * h;

        if (!!f) {
          this._map.Df(this._map.Ob() + .01 * moveZ, event.viewportX, event.viewportY);
        } else {
          const viewMovel = this._map.getViewModel();
          viewMovel.startControl({ duration: 1000, power: 1 }, event.viewportX, event.viewportY);
          viewMovel.control(0, 0, moveZ, 0, 0, 0);
          clearTimeout(controlTimeout);
          controlTimeout = setTimeout(() => {
            viewMovel.endControl(true);
          }, 5);
        }
      }

      event.preventDefault();
    });
  }

  private _revealCluster(params?: { lat: number; lng: number; expansionZoom: number}) {
    if (params) {
      const { lat, lng, expansionZoom } = params
      this._map.getViewModel().setLookAtData(
        {
          position: { lat, lng },
          zoom: expansionZoom + 1
        },
        2
      );
    }
  }

  // _getVehicleRestrictionsLayer() {
  //   const currentLayer = this._layers.get('VehicleRestrictionsLayer');
  //   if (currentLayer) {
  //     return currentLayer;
  //   }

  //   const rasterTile = this._platform.getRasterTileService({
  //     resource: 'blank',
  //     queryParams: {
  //       style: 'logistics.day',
  //       features: 'vehicle_restrictions:permanent_only',
  //       size: 512,
  //       lang: this._lang,
  //       ppi: 100,
  //     },
  //   });
  //   // @ts-ignore
  //   const resterTileProvider = new window.H.service.rasterTile.Provider(rasterTile);

  //   const tileLayer = new window.H.map.layer.TileLayer(resterTileProvider);
  //   this._layers.set('VehicleRestrictionsLayer', tileLayer);
  //   return tileLayer;
  // }

  _getSatelliteMap() {
    const currentLayer = this._layers.get('satelliteLayer');
    if (currentLayer) {
      return currentLayer;
    }
    const rasterTileService = this._platform.getRasterTileService({
      queryParams: {
        style: 'explore.satellite.day',
        features: this._mapFeatures,
        size: this._defineTileSize(),
        lang: this._lang,
        ppi: 100
      },
    });
    // @ts-ignore
    const rasterTileProvider = new window.H.service.rasterTile.Provider(rasterTileService);
    const tileLayer = new window.H.map.layer.TileLayer(rasterTileProvider);
    // @ts-ignore
    tileLayer.tileStyle = 'explore.satellite.day';
    this._layers.set('satelliteLayer', tileLayer);
    return tileLayer;
  }

  _defineTileSize() {
    return this._pixelRatio === 1 ? 256 : 512;
  }

  _initMapUi(defaultLayers: any) {
    if (this._map) {
      this._mapUI = window.H.ui.UI.createDefault(this._map, defaultLayers);
      this._mapUI.removeControl('zoom');
      this._mapUI.removeControl('mapsettings');
    }
  }

  _resizeMap() {
    // Enable dynamic resizing of the map, based on the current size of the enclosing container
    window.addEventListener('resize', () => {
      if (this._map) {
        this._map.getViewPort().resize();
      }
    });
  }

  _enableMapEvents() {
    if (this._map) {
      new window.H.mapevents.Behavior(new window.H.mapevents.MapEvents(this._map));
    }
  }

  get mapUi() {
    return this._mapUI;
  }

  addBubble(position: H.geo.IPoint, options: H.ui.InfoBubble.Options): void {
    const bubble = new H.ui.InfoBubble(position, options);
    this._mapUI.addBubble(bubble);
  }

  getNativeMap(): Here | undefined {
    return this._map;
  }

  getPlatform(): any {
    return this._platform;
  }

  fitBounds({ sw, ne }: FitBounds, padding: MapPadding = {}) {
    const { top = 0, right = 0, bottom = 0, left = 0 } = padding;
    this._map.getViewPort().setPadding(top, right, bottom, left);
    this._map.getViewPort().resize();
    if (ne && sw) {
      const rect = new window.H.geo.Rect(ne?.lat, sw?.lng, sw?.lat, ne?.lng);
      this._map.getViewModel().setLookAtData(
        {
          position: rect.getCenter(),
          bounds: rect.getBoundingBox()
        },
        1.25
      );
    }
  }

  zoomIn() {
    if (this._map) {
      const currentZoom = this._map.getZoom() || 0;
      this._map.setZoom(currentZoom + 1);
    }
  }

  zoomOut() {
    if (this._map) {
      const currentZoom = this._map.getZoom() || 0;
      this._map.setZoom(currentZoom - 1);
    }
  }

  setZoom(value: Zoom) {
    this._map?.setZoom(value);
  }

  getZoom() {
    if (this._map) {
      return this._map.getZoom() || 0;
    }
  }

  setCenter({ lat, lng }: CenterCoords = {}) {
    this._map?.setCenter({
      lat,
      lng,
    });
  }

  setMapType(mapType: MapType) {
    let layer = null;

    if (mapType === MAP_TYPES.ROADMAP) {
      layer = this._defaultLayers.vector.normal.map;
    }

    if (mapType === MAP_TYPES.HYBRID) {
      layer = this._getSatelliteMap();
    }

    if (layer === null) {
      return;
    }

    this._map.setBaseLayer(layer);
  }

  enableTrafficLayer() {
    this._map.addLayer(this._defaultLayers.vector.traffic.map, 1);
  }

  disableTrafficLayer() {
    this._map.removeLayer(this._defaultLayers.vector.traffic.map);
  }

  addListenerDragStart(fn: OnMapEventListener): MapEventListener {
    return undefined;
  }

  addListenerBoundsChanged(fn: OnChangeBounds): MapEventListener {
    return undefined;
  }

  addListenerStreetViewChange(fn: OnChangeMapStreetView): MapEventListener {
    return undefined;
  }

  addListenerMapViewChange(fn: OnChangeMapStreetView): MapEventListener {
    return this._map.addEventListener('mapviewchange', fn);
  }
  removeListenerMapViewChange(fn: OnChangeMapStreetView): MapEventListener {
    return this._map.removeEventListener('mapviewchange', fn);
  }

  addClickListener(listener: ClickListener) {
    this._clickListeners.push(listener);
  }

  removeClickListener(listener: ClickListener) {
    this._clickListeners = this._clickListeners.filter(item => item !== listener);
  }
}
